import { useCallback, useEffect, useState } from 'react';
import { Badge, Note, Panel, Spinner, type Tone } from '@wyocspay/ui';
import { ApiError, api, type MethodList, type MethodState } from './api.js';

/**
 * Which payment methods this merchant accepts.
 *
 * Backed by a Stripe Payment Method Configuration, not by a local list: the
 * configuration id is attached to every PaymentIntent, so what is ticked here
 * is what the customer is actually offered. A page that only recorded
 * preferences would be worse than no page.
 *
 * Two things this screen used to get wrong, both fixed here.
 *
 * It answered every unavailable method with the same sentence - "it may need
 * activating with Stripe, or it may not support this currency" - which is the
 * platform speculating about its own account. Stripe does not leave anyone
 * guessing: each capability carries a status and its own requirements, and the
 * account carries a `disabled_reason`. The server reads those and this page
 * prints them. A merchant told "Stripe needs two more details from you" does
 * something; one told "it may be one of two things" does nothing.
 *
 * And it printed that sentence fifteen times on an account whose real problem
 * was one sentence: verification was not finished, so nothing could be active,
 * cards included. When that is the case it is now said once, at the top, with
 * the way to fix it, and the list below goes quiet rather than shouting the
 * same thing fifteen times over.
 */

/**
 * The four things a row can be, each with a word rather than only a colour.
 *
 * `chosen-not-live` is the one that used to be rendered as a contradiction - a
 * greyed "On" next to "not available". A method the merchant has chosen but
 * which cannot currently run is neither on nor off, and it looks like neither.
 */
type RowState = 'account-blocked' | 'live' | 'off' | 'chosen-not-live' | 'unavailable';

const ROW_LABEL: Record<RowState, { text: string; tone: Tone }> = {
  'account-blocked': { text: 'NOT YET', tone: 'neutral' },
  live: { text: 'LIVE', tone: 'ok' },
  off: { text: 'OFF', tone: 'neutral' },
  'chosen-not-live': { text: 'ON, NOT LIVE', tone: 'warn' },
  unavailable: { text: 'UNAVAILABLE', tone: 'neutral' },
};

function rowState(method: MethodState, accountBlocked: boolean): RowState {
  if (accountBlocked) return 'account-blocked';
  if (method.available) return method.enabled ? 'live' : 'off';
  return method.enabled ? 'chosen-not-live' : 'unavailable';
}

export function PaymentMethodsView({
  onNavigate,
}: {
  /** Somewhere to send a merchant whose account is the thing holding them up. */
  onNavigate?: (view: string) => void;
}) {
  const [data, setData] = useState<MethodList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.paymentMethods());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every method on the page, flattened, so a row can be found by key. */
  function findMethod(key: string): MethodState | null {
    if (!data) return null;
    for (const group of data.groups) {
      const hit = group.methods.find((m) => m.key === key);
      if (hit) return hit;
    }
    return data.other.find((m) => m.key === key) ?? null;
  }

  /**
   * Turn a method on or off, and say what that actually did.
   *
   * Switching on splits three ways, and answering all three with "Enabled" was
   * the reason this screen could not be trusted. A method whose capability was
   * never requested cannot activate however it is ticked, so pressing on asks
   * Stripe for it first - the request is idempotent, so it costs nothing when
   * the capability is already there. One that Stripe is still checking is
   * recorded and will appear by itself. One that Stripe has refused pending
   * something from the merchant needs that thing, and the message says so and
   * offers the way to provide it rather than leaving them to find it.
   */
  async function toggle(key: string, next: boolean): Promise<void> {
    const method = findMethod(key);
    setBusy(key);
    setMsg(null);
    setError(null);
    try {
      /**
       * Asking for the capability before recording the choice.
       *
       * Only when Stripe has never been asked. Recording "on" against a
       * capability in that state would be a preference that can never take
       * effect, which is the same dead end in a different disguise.
       */
      if (next && method?.capabilityStatus === 'unrequested') {
        await api.requestCapabilities().catch(() => undefined);
      }

      await api.updatePaymentMethods({ [key]: next });

      if (!next) {
        setMsg('Switched off. It will not be offered on new payments, and you can switch it back on here whenever you like.');
      } else if (method && !method.available) {
        setMsg(
          method.capabilityStatus === 'pending'
            ? 'Recorded. Stripe is still checking this one - it will start appearing at checkout by itself once approved, with nothing further from you.'
            : 'Recorded. Stripe has not activated this one yet, so it will not appear at checkout until it does. If Stripe needs something from you, it is on the Verification page.',
        );
      } else {
        setMsg('Enabled. It will appear at checkout on the next payment.');
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Clear the platform's own omission.
   *
   * Every connected account used to be created asking Stripe for cards and
   * transfers only, so most of the methods below were never available to
   * anybody. That is WyoCSPay's mistake rather than the merchant's, which is
   * why it reads as a repair being offered rather than a task being assigned.
   */
  async function requestMissing(): Promise<void> {
    setRequesting(true);
    setMsg(null);
    setError(null);
    try {
      const result = await api.requestCapabilities();
      if (!result.changed) {
        setMsg('Everything available for your country had already been requested.');
      } else {
        const parts = [`Asked Stripe to enable ${result.requested.length} more.`];
        if (result.newly_due.length > 0) {
          parts.push(
            `Stripe now needs ${result.newly_due.length} further ${
              result.newly_due.length === 1 ? 'detail' : 'details'
            } about your business - see Onboarding.`,
          );
        }
        if (result.refused.length > 0) {
          parts.push(`${result.refused.length} are not offered in your country.`);
        }
        parts.push('Activation can take a few minutes.');
        setMsg(parts.join(' '));
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRequesting(false);
    }
  }

  if (error && !data) return <Note tone="danger">{error}</Note>;
  if (!data) return <Spinner label="Loading…" />;

  const blocked = data.account.blocked_reason;
  const missing = data.account.missing_capabilities.length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/*
        The one thing worth reading, when it applies.

        Nothing on this account can be active until Stripe has verified the
        business, so this replaces the per-row warnings rather than sitting
        above them.
      */}
      {blocked ? (
        <Note tone="warn" title="Nothing here can go live yet">
          {blocked}
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              onClick={() => onNavigate?.('onboarding')}
              disabled={!onNavigate}
            >
              Finish verification
            </button>
            {data.account.requirements_due.length > 0 ? (
              <span style={{ marginLeft: 10, fontSize: 12.5, color: 'var(--wc-muted)' }}>
                {data.account.requirements_due.length} outstanding{' '}
                {data.account.requirements_due.length === 1 ? 'item' : 'items'}
              </span>
            ) : null}
          </div>
        </Note>
      ) : (
        <Note title="What your customers see">
          Turning a method on here adds it to the payment page. Stripe decides
          which of them can actually be shown for a given payment - the amount,
          the currency and the customer's country all narrow the list, so a
          method that is on will not always appear.
        </Note>
      )}

      {/*
        The platform's own gap, stated as ours.

        Not phrased as something the merchant failed to do, because it is not:
        WyoCSPay never asked Stripe for these capabilities.
      */}
      {missing > 0 ? (
        <Note tone="warn" title={`${missing} ${missing === 1 ? 'method has' : 'methods have'} never been requested`}>
          WyoCSPay has not asked Stripe to enable{' '}
          {missing === 1 ? 'this one' : 'these'} on your account, so{' '}
          {missing === 1 ? 'it cannot' : 'they cannot'} be offered whatever is ticked below. That is
          ours to fix, not yours.
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              onClick={() => void requestMissing()}
              disabled={requesting}
            >
              {requesting ? 'Asking Stripe…' : 'Ask Stripe to enable them'}
            </button>
          </div>
        </Note>
      ) : null}

      {msg ? <Note tone="ok">{msg}</Note> : null}
      {error ? <Note tone="danger">{error}</Note> : null}

      {data.configuration_id === null ? (
        <Note tone="warn">
          You are on Stripe's default selection. Changing anything below creates
          your own configuration, starting from exactly what is in force now.
        </Note>
      ) : null}

      {data.groups.map((group) => (
        <Panel key={group.group} title={group.group}>
          <div style={{ display: 'grid', gap: 2 }}>
            {group.methods.map((m) => (
              <MethodRow
                key={m.key}
                method={m}
                accountBlocked={Boolean(blocked)}
                busy={busy === m.key}
                onToggle={toggle}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </Panel>
      ))}

      {data.other.length > 0 ? (
        <Panel title="Other methods on your account">
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
            Stripe knows about these but WyoCSPay does not group them. They are
            shown so nothing is enabled on your account that you cannot see or
            turn off.
          </p>
          <div style={{ display: 'grid', gap: 2 }}>
            {data.other.map((m) => (
              <MethodRow
                key={m.key}
                method={m}
                accountBlocked={Boolean(blocked)}
                busy={busy === m.key}
                onToggle={toggle}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function MethodRow({
  method,
  accountBlocked,
  busy,
  onToggle,
  onNavigate,
}: {
  method: MethodState;
  accountBlocked: boolean;
  busy: boolean;
  onToggle: (key: string, next: boolean) => void;
  onNavigate?: (view: string) => void;
}) {
  const locked = method.key === 'card';
  const state = rowState(method, accountBlocked);
  const badge = ROW_LABEL[state];

  /**
   * The reason, coloured by who has to act on it.
   *
   * A merchant-owned reason is the only one worth an alarming colour: it is the
   * only one they can do anything about. A capability WyoCSPay never requested,
   * or one Stripe is still checking, is stated plainly and left alone.
   */
  const reasonColour =
    method.guessed ? 'var(--wc-faint)'
    : method.owner === 'merchant' ? 'var(--wc-amber)'
    : method.owner === 'platform' ? 'var(--wc-blue-ink)'
    : 'var(--wc-muted)';

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid var(--wc-border)',
        // Inert rather than alarmed: the reason is at the top of the page.
        opacity: accountBlocked ? 0.6 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ fontSize: 14 }}>
          {method.label}
          {locked ? (
            <Badge tone="neutral" icon={false}>
              ALWAYS ON
            </Badge>
          ) : null}
        </strong>
        {method.note ? (
          <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{method.note}</div>
        ) : null}

        {/*
          Only ever Stripe's answer, or a marked-up absence of one. Nothing is
          printed at all while the account itself is the blocker.
        */}
        {!accountBlocked && method.unavailableReason ? (
          <div
            style={{
              fontSize: 12.5,
              color: reasonColour,
              fontStyle: method.guessed ? 'italic' : 'normal',
              marginTop: 2,
            }}
          >
            {method.unavailableReason}
            {method.owner === 'merchant' && onNavigate ? (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => onNavigate('onboarding')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'var(--wc-blue)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Provide it
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <span style={{ flex: 'none' }} title={method.capabilityStatus ?? undefined}>
        <Badge tone={badge.tone}>{badge.text}</Badge>
      </span>

      {/*
        The switch works in both directions, always. It is a choice, not a status.
      *
        Two earlier versions of this were each wrong in one direction. The first
        refused to switch anything unavailable, so a merchant who had chosen Link
        could not un-choose it. The second allowed off but still refused on,
        which turned every off into a one-way door: switch off a method whose
        capability is pending - Klarna, the day it is requested - and the control
        dies in your hand with nothing to press and nothing said.
      *
        Neither had to happen, because `rowState` above already models the answer.
        What is ticked here is the merchant's preference and what Stripe has
        activated is a separate fact; "ON, NOT LIVE" is precisely the state where
        the two disagree, and it is a legitimate state to ask for. Choosing a
        method before it activates means it appears the moment it does, without
        anybody having to come back and notice.
      *
        Only two things still refuse. Cards, because a payment page without them
        is not a payment page. And an account Stripe has not verified, where the
        reason is stated once at the top rather than fifteen times down the list.
      */}
      <button
        type="button"
        className={`wc-btn wc-btn-sm ${method.enabled ? '' : 'wc-btn-secondary'}`}
        onClick={() => onToggle(method.key, !method.enabled)}
        disabled={busy || locked || accountBlocked}
        aria-pressed={method.enabled}
        aria-label={`${method.label}: ${badge.text.toLowerCase()}`}
        style={{ minWidth: 74, flex: 'none' }}
      >
        {busy ? '…' : method.enabled ? 'On' : 'Off'}
      </button>
    </div>
  );
}
